/**
 * Phase 1 item 7 (AC6) — Coordinator "Notify all pending re-consent"
 * button + confirm modal + results panel.
 *
 * Two-step UX:
 *   1. Dry-run: enumerate + preview outcomes without dispatching email.
 *   2. Confirmed run: fire the notifications and show per-subject
 *      results (notified vs skipped-with-reason).
 *
 * Drop this button into any study-scoped surface (Study Workspace
 * Hub, Compliance Hub, Subject list) by passing `studyId`.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MailPlus,
  X,
  XCircle,
} from 'lucide-react';
import {
  bulkReConsentNotify,
  type BulkNotifyOutcome,
  type BulkNotifySkippedReason,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';

const NOTIFY_ROLES = [
  'coordinator',
  'regulatory_specialist',
] as const;

const SKIPPED_LABEL: Record<BulkNotifySkippedReason, string> = {
  no_email_on_file: 'No email on file',
  not_flagged_for_reconsent: 'Not flagged for re-consent',
  terminal_status: 'Terminal status (withdrawn/completed)',
  cross_site: 'Cross-site (defensive skip)',
  not_in_allowlist: 'Not in explicit allowlist',
  send_failed: 'Send failed (SES/retry)',
};

export function BulkReConsentNotifyButton({ studyId, readOnly = false }: { studyId: string; readOnly?: boolean }) {
  const canNotify = useHasRole(NOTIFY_ROLES) && !readOnly;
  const [open, setOpen] = useState(false);
  if (!canNotify) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Send a heads-up email to every subject on this study flagged for re-consent"
        className="btn-secondary inline-flex items-center gap-1 text-xs"
      >
        <MailPlus className="h-3.5 w-3.5" />
        Notify re-consent
      </button>
      {open && (
        <BulkReConsentNotifyModal
          studyId={studyId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function BulkReConsentNotifyModal({
  studyId,
  onClose,
}: {
  studyId: string;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<{
    notified_count: number;
    candidates_considered: number;
    version_label: string;
    outcomes: BulkNotifyOutcome[];
  } | null>(null);
  const [confirmedRun, setConfirmedRun] = useState<{
    notified_count: number;
    outcomes: BulkNotifyOutcome[];
  } | null>(null);

  const dryRun = useMutation({
    mutationFn: () => bulkReConsentNotify.run(studyId, { dry_run: true }),
    onSuccess: (r) => setPreview(r),
    onError: (e) => toast.error(errorText(e, 'Preview failed')),
  });
  const send = useMutation({
    mutationFn: () => bulkReConsentNotify.run(studyId, { dry_run: false }),
    onSuccess: (r) => {
      toast.success(
        `Notified ${r.notified_count} of ${r.candidates_considered} candidates.`,
      );
      setConfirmedRun({ notified_count: r.notified_count, outcomes: r.outcomes });
    },
    onError: (e) => toast.error(errorText(e, 'Send failed')),
  });

  const showOutcomes = confirmedRun ?? preview;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
              <MailPlus className="h-4 w-4 text-brand-500" />
              Notify pending re-consent subjects
            </h3>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Sends a heads-up email to every subject on this study flagged for
              re-consent. Per-subject signing links go out separately via the
              standard remote-consent flow.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4 text-xs">
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            Recipient scope: subjects on this study at your active site,
            <strong> flagged for re-consent</strong>, with an email on file.
            Withdrawn / completed subjects are skipped.
          </div>

          {!showOutcomes ? (
            <div className="rounded border border-surface-border bg-surface-muted/20 p-3 text-center">
              <p className="mb-2 text-[11px] text-ink-muted">
                Preview the recipient list before dispatching any email.
              </p>
              <button
                type="button"
                onClick={() => dryRun.mutate()}
                disabled={dryRun.isPending}
                className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
              >
                {dryRun.isPending ? (
                  <>
                    <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                    Enumerating…
                  </>
                ) : (
                  'Preview recipients'
                )}
              </button>
            </div>
          ) : (
            <>
              <div className="rounded border border-surface-border bg-surface-card p-2">
                <p className="text-[11px] text-ink-muted">
                  {confirmedRun ? 'Send complete.' : 'Preview'}
                  {preview?.version_label && (
                    <>
                      {' '}
                      · Consent version{' '}
                      <span className="font-mono">{preview.version_label}</span>
                    </>
                  )}
                </p>
                <p className="mt-1 text-[13px] font-semibold text-ink-primary">
                  {showOutcomes.notified_count} of{' '}
                  {preview?.candidates_considered ?? showOutcomes.outcomes.length}{' '}
                  candidate{showOutcomes.outcomes.length === 1 ? '' : 's'}{' '}
                  {confirmedRun ? 'notified' : 'would receive an email'}.
                </p>
              </div>
              <ul className="max-h-56 space-y-1 overflow-y-auto pr-1">
                {showOutcomes.outcomes.map((o) => (
                  <li
                    key={o.subject_id}
                    className={`flex items-center justify-between rounded border p-1.5 text-[11px] ${o.status === 'notified'
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : 'border-amber-200 bg-amber-50/50'
                      }`}
                  >
                    <span className="font-mono text-[11px] text-ink-secondary">
                      {o.subject_display_id ?? o.external_ref}
                    </span>
                    <span className="flex items-center gap-1 text-[10px]">
                      {o.status === 'notified' ? (
                        <>
                          <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
                          {confirmedRun ? 'notified' : 'would notify'}
                        </>
                      ) : (
                        <>
                          <XCircle className="h-2.5 w-2.5 text-amber-600" />
                          {o.reason ? SKIPPED_LABEL[o.reason] : 'skipped'}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Close
          </button>
          {preview && !confirmedRun && (
            <button
              type="button"
              onClick={() => send.mutate()}
              disabled={preview.notified_count === 0 || send.isPending}
              className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {send.isPending ? (
                <>
                  <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                  Sending…
                </>
              ) : (
                `Send to ${preview.notified_count}`
              )}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
